// src/components/messaging/NewSmsModal.tsx
// Modal for starting a new SMS conversation from the Unibox.
// Uses the Unibox createThread + sendMessage path (no telephony capability required).

import { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {
  useCreateThreadMutation,
  useSendMessageMutation,
  useCheckSmsAvailableQuery,
} from '../../api/uniboxApi';
import { useActiveWorkspace } from '../../hooks/useActiveWorkspace';

interface NewSmsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NewSmsModal({ isOpen, onClose }: NewSmsModalProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const activeWorkspace = useActiveWorkspace();
  const [createThread] = useCreateThreadMutation();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  // B2 — truthful availability. Distinguishes native vs Outbound-managed mirror
  // vs unavailable, so we never show an enabled composer that will fail at send.
  const { data: availability, isLoading: isCheckingAvailability } =
    useCheckSmsAvailableQuery(undefined, { skip: !isOpen });
  const smsUnavailable = availability?.available === false;
  const managedByOutbound = availability?.managedByOutbound === true;

  const handleSend = async () => {
    setError(null);
    setSuccess(false);

    const trimmedPhone = phoneNumber.trim();
    const trimmedBody = messageBody.trim();

    if (!trimmedPhone) {
      setError('Phone number is required');
      return;
    }
    if (!trimmedBody) {
      setError('Message body is required');
      return;
    }
    if (trimmedBody.length > 1600) {
      setError('SMS messages cannot exceed 1600 characters');
      return;
    }

    try {
      // Create or reuse an existing thread for this phone number
      const thread = await createThread({
        workspaceId: activeWorkspace?.workspace.id,
        toAddress: trimmedPhone,
        medium: 'sms',
      }).unwrap();

      // Send the SMS via the thread
      await sendMessage({
        threadId: thread.id,
        medium: 'sms',
        toAddress: trimmedPhone,
        bodyText: trimmedBody,
      }).unwrap();

      setSuccess(true);
      setPhoneNumber('');
      setMessageBody('');
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      const e = err as { data?: { message?: string; error?: string } };
      setError(
        e?.data?.message ||
        e?.data?.error ||
        'Failed to send SMS. Check your network and try again.'
      );
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    setMessageBody('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="New SMS" size="md">
      <div className="space-y-4">
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            SMS sent successfully!
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            {error}
          </div>
        )}

        {/* B2 — provider availability banners. */}
        {smsUnavailable && (
          <div
            data-testid="sms-unavailable-banner"
            className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
          >
            {availability?.reason === 'suspended'
              ? 'The Twilio SMS provider is suspended in Outbound. Reactivate it there to send.'
              : availability?.reason === 'missing_from_number'
                ? 'The Outbound-managed Twilio SMS provider has no sender number configured.'
                : 'No Twilio SMS provider is configured for this workspace.'}
          </div>
        )}
        {!smsUnavailable && managedByOutbound && (
          <div
            data-testid="sms-managed-by-outbound-banner"
            className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800"
          >
            Sending via your Outbound-managed Twilio provider
            {availability?.fromNumber ? ` (${availability.fromNumber})` : ''}. Credentials stay in Outbound.
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            To (Phone Number)
          </label>
          <input
            type="tel"
            placeholder="+1 555 123 4567"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            rows={4}
            placeholder="Type your SMS message..."
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            maxLength={1600}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <div className="mt-1 text-right text-xs text-gray-400">
            {messageBody.length}/1600
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              isSending ||
              isCheckingAvailability ||
              smsUnavailable ||
              !phoneNumber.trim() ||
              !messageBody.trim()
            }
            isLoading={isSending}
          >
            Send SMS
          </Button>
        </div>
      </div>
    </Modal>
  );
}
